import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  CheckCircle2,
  Circle,
  Zap,
  Bot,
  MessageCircle,
  BarChart3,
  Gift,
  Users,
  Crown,
  Sparkles,
  ArrowRight,
  Facebook,
  Instagram,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import DashboardHeroBackground from "@/components/DashboardHeroBackground";
import { toast } from "sonner";

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Bom dia";
  if (h < 18) return "Boa tarde";
  return "Boa noite";
}

export default function HomePage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [leadsHoje, setLeadsHoje] = useState(null);
  const [leadsMes, setLeadsMes] = useState(null);
  const [pipelineCount, setPipelineCount] = useState(null);
  const [agentConfig, setAgentConfig] = useState(null);
  const [waConnected, setWaConnected] = useState(null);
  const [metaAdsConnected, setMetaAdsConnected] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.allSettled([
      api.get("/prospect/agent/today"),
      api.get("/prospect/stats"),
      api.get("/prospect/pipeline"),
      api.get("/prospect/agent/config"),
      api.get("/whatsapp/status"),
      api.get("/meta-ads/status"),
    ]).then(([todayR, statsR, pipeR, cfgR, waR, metaR]) => {
      if (todayR.status === "fulfilled")
        setLeadsHoje(todayR.value.data.sent_today ?? 0);
      if (statsR.status === "fulfilled")
        setLeadsMes(statsR.value.data.used ?? statsR.value.data.used_this_month ?? 0);
      if (pipeR.status === "fulfilled")
        setPipelineCount((pipeR.value.data.leads || []).length);
      if (cfgR.status === "fulfilled")
        setAgentConfig(cfgR.value.data);
      if (waR.status === "fulfilled") {
        const d = waR.value.data;
        setWaConnected(d?.status === "connected");
      }
      if (metaR.status === "fulfilled") {
        setMetaAdsConnected(metaR.value.data?.connected === true);
      } else {
        setMetaAdsConnected(false);
      }
    });
  }, []);

  const toggleSocial = async (platform, connected) => {
    setBusy(true);
    try {
      if (connected) {
        await api.post(`/auth/disconnect/${platform}`);
        toast.info(`${platform === "facebook" ? "Facebook" : "Instagram"} desconectado`);
      } else {
        await api.post(`/auth/connect/${platform}`);
        toast.success(`${platform === "facebook" ? "Facebook" : "Instagram"} conectado`);
      }
      await refreshUser();
    } catch {
      toast.error("Não foi possível realizar a ação.");
    } finally {
      setBusy(false);
    }
  };

  const isPro = !user?.is_admin && (user?.plan_key === "pro" || user?.plan_key === "starter");
  const isAgency = !user?.is_admin && user?.plan_key === "agency";
  const isFree = !user?.is_admin && (!user?.plan_key || user?.plan_key === "free");

  const checklist = [
    {
      label: "WhatsApp conectado",
      done: waConnected === true,
      route: "/whatsapp",
      cta: "Conectar",
    },
    {
      label: "Agente configurado (cidade + segmento de busca)",
      done: !!(agentConfig?.query && agentConfig?.cidade),
      route: "/prospecto",
      cta: "Configurar",
    },
    {
      label: "Bairros de rotação adicionados (mínimo 3)",
      done: (agentConfig?.bairros_list || []).length >= 3,
      route: "/prospecto",
      cta: "Adicionar",
    },
    {
      label: "Piloto automático ativado",
      done: agentConfig?.enabled === true,
      route: "/prospecto",
      cta: "Ativar",
    },
  ];
  const checklistDone = checklist.filter((c) => c.done).length;
  const isOnboardingComplete = checklistDone === checklist.length;

  const shortcuts = [
    { label: "Disparar Posts", icon: Zap, route: "/painel", color: "#3b82f6" },
    { label: "Estratégias de Vendas", icon: Bot, route: "/prospecto", color: "#a78bfa" },
    { label: "WhatsApp Bot", icon: MessageCircle, route: "/whatsapp", color: "#22c55e" },
    { label: "Grupos & Páginas", icon: Users, route: "/grupos", color: "#f59e0b" },
    { label: "Meta Ads", icon: BarChart3, route: "/meta-ads", color: "#ec4899" },
    { label: "Afiliados", icon: Gift, route: "/afiliados", color: "#06b6d4" },
  ];

  const planIcon = user?.is_admin ? "👑" : isAgency ? "🚀" : isPro ? "⚡" : "✨";
  const planColor = user?.is_admin
    ? "#f59e0b"
    : isAgency
    ? "#a78bfa"
    : isPro
    ? "#3b82f6"
    : "rgba(255,255,255,0.5)";

  return (
    <div style={{ position: "relative", minHeight: "100vh" }}>
      <DashboardHeroBackground />

      <div style={{ position: "relative", zIndex: 1, padding: "24px 24px 48px", maxWidth: 920 }}>
        {/* Cabeçalho */}
        <div style={{ marginBottom: 28 }}>
          <p style={{ fontSize: 13, color: "rgba(255,255,255,0.65)", marginBottom: 2, margin: 0 }}>
            {getGreeting()},{" "}
            <strong style={{ color: "#ffffff" }}>
              {user?.name?.split(" ")[0] || "usuário"}
            </strong>{" "}
            👋
          </p>
          <h1
            style={{
              fontSize: 26,
              fontWeight: 700,
              color: "#ffffff",
              margin: "4px 0 2px",
              textShadow: "0 2px 12px rgba(0,0,0,0.4)",
            }}
          >
            Painel Inicial
          </h1>
          <p style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", margin: 0 }}>
            Resumo da sua operação e atalhos rápidos
          </p>
        </div>

      {/* ── Status das conexões ── */}
      <SectionLabel>Status das conexões</SectionLabel>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
          gap: 10,
          marginBottom: 24,
        }}
      >
        {/* WhatsApp */}
        <ConnCard
          emoji={<img src="/whatsapp.png" alt="WhatsApp" style={{ height: 20, width: 20 }} />}
          label="WhatsApp"
          badge={
            waConnected === null
              ? { text: "verificando…", color: "rgba(255,255,255,0.5)" }
              : waConnected
              ? { text: "CONECTADO", color: "#22c55e" }
              : { text: "DESCONECTADO", color: "#ef4444" }
          }
          action={
            waConnected === false
              ? { label: "Conectar", onClick: () => navigate("/whatsapp") }
              : null
          }
        />

        {/* Facebook (Meta Ads) */}
        <ConnCard
          emoji={<img src="/facebook.png" alt="Facebook" style={{ height: 20, width: 20 }} />}
          label="Facebook"
          badge={
            metaAdsConnected === null
              ? { text: "verificando…", color: "rgba(255,255,255,0.5)" }
              : metaAdsConnected
              ? { text: "CONECTADO", color: "#22c55e" }
              : { text: "DESCONECTADO", color: "rgba(255,255,255,0.5)" }
          }
          action={
            metaAdsConnected === false
              ? { label: "Conectar", onClick: () => navigate("/meta-ads") }
              : null
          }
        />

        {/* Instagram */}
        <ConnCard
          emoji={<img src="/instagram.png" alt="Instagram" style={{ height: 20, width: 20 }} />}
          label="Instagram"
          badge={
            user?.ig_connected
              ? { text: "CONECTADO", color: "#22c55e" }
              : { text: "DESCONECTADO", color: "rgba(255,255,255,0.5)" }
          }
          action={{
            label: user?.ig_connected ? "Desconectar" : "Conectar",
            onClick: () => toggleSocial("instagram", user?.ig_connected),
            busy,
            outline: user?.ig_connected,
          }}
        />

        {/* Plano */}
        <ConnCard
          emoji={planIcon}
          label="Plano atual"
          badge={{ text: user?.is_admin ? "ADMIN" : (user?.plan_name || "Grátis").toUpperCase(), color: planColor }}
          action={
            !user?.is_admin
              ? {
                  label: isFree ? "Fazer upgrade" : "Ver planos",
                  onClick: () => navigate("/planos"),
                }
              : null
          }
        />
      </div>

      {/* ── Métricas de prospecção ── */}
      <SectionLabel>Métricas de prospecção</SectionLabel>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
          gap: 10,
          marginBottom: 24,
        }}
      >
        <MetricCard
          icon="🎯"
          label="Leads abordados hoje"
          value={leadsHoje}
          suffix="/ 50"
          color="#a78bfa"
        />
        <MetricCard
          icon="📅"
          label="Leads este mês"
          value={leadsMes}
          color="#3b82f6"
        />
        <MetricCard
          icon="🏆"
          label="Total no pipeline"
          value={pipelineCount}
          color="#22c55e"
          onClick={() => navigate("/prospecto")}
        />
        <MetricCard
          icon="🤖"
          label="Piloto automático"
          value={
            agentConfig === null ? null : agentConfig?.enabled ? "ON" : "OFF"
          }
          color={agentConfig?.enabled ? "#22c55e" : "rgba(255,255,255,0.5)"}
          isText
          onClick={() => navigate("/prospecto")}
        />
      </div>

      {/* ── Onboarding checklist (só aparece enquanto incompleto) ── */}
      {!isOnboardingComplete && (
        <>
          <SectionLabel>Configure sua operação</SectionLabel>
          <div
            style={{
              background: "rgba(59,130,246,0.06)",
              border: "1px solid rgba(59,130,246,0.2)",
              borderRadius: 12,
              padding: "16px 18px",
              marginBottom: 24,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 10,
              }}
            >
              <span style={{ fontSize: 12.5, fontWeight: 700, color: "#93c5fd" }}>
                🚀 {checklistDone} de {checklist.length} etapas concluídas
              </span>
              <span style={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>
                {Math.round((checklistDone / checklist.length) * 100)}% completo
              </span>
            </div>

            {/* barra de progresso */}
            <div
              style={{
                height: 4,
                borderRadius: 99,
                background: "rgba(255,255,255,0.07)",
                overflow: "hidden",
                marginBottom: 14,
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${(checklistDone / checklist.length) * 100}%`,
                  background: "linear-gradient(90deg,#3b82f6,#a78bfa)",
                  borderRadius: 99,
                  transition: "width 0.4s ease",
                }}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {checklist.map((item, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {item.done ? (
                    <CheckCircle2 size={16} style={{ color: "#22c55e", flexShrink: 0 }} />
                  ) : (
                    <Circle size={16} style={{ color: "rgba(255,255,255,0.3)", flexShrink: 0 }} />
                  )}
                  <span
                    style={{
                      flex: 1,
                      fontSize: 13,
                      color: item.done ? "rgba(255,255,255,0.45)" : "#ffffff",
                      textDecoration: item.done ? "line-through" : "none",
                    }}
                  >
                    {item.label}
                  </span>
                  {!item.done && (
                    <button
                      onClick={() => navigate(item.route)}
                      style={{
                        fontSize: 11.5,
                        fontWeight: 600,
                        color: "#60a5fa",
                        background: "rgba(59,130,246,0.1)",
                        border: "1px solid rgba(59,130,246,0.3)",
                        borderRadius: 6,
                        padding: "3px 11px",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.cta} →
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── Acesso rápido ── */}
      <SectionLabel>Acesso rápido</SectionLabel>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
          gap: 8,
          marginBottom: 24,
        }}
      >
        {shortcuts.map((s, i) => (
          <ShortcutBtn key={i} {...s} />
        ))}
      </div>

      {/* ── Dados da conta ── */}
      <SectionLabel>Dados da conta</SectionLabel>
      <div
        style={{
          background: "rgba(255,255,255,0.04)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 12,
          padding: "14px 18px",
          boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: "8px 24px",
            fontSize: 13,
            marginBottom: 12,
          }}
        >
          <AccountRow label="Nome" value={user?.name} />
          <AccountRow label="E-mail" value={user?.email} />
          <AccountRow
            label="Membro desde"
            value={
              user?.created_at
                ? new Date(user.created_at).toLocaleDateString("pt-BR")
                : "—"
            }
          />
          <AccountRow label="ID da conta" value={user?.id} mono />
        </div>

        {!user?.is_admin && user?.plan_key !== "free" && (
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 10 }}>
            <button
              onClick={async () => {
                if (!window.confirm("Cancelar plano e voltar para o plano Grátis?")) return;
                try {
                  await api.post("/billing/cancel");
                  toast.info("Plano cancelado. Você está no plano Grátis.");
                  await refreshUser();
                } catch {
                  toast.error("Não foi possível cancelar o plano.");
                }
              }}
              style={{
                fontSize: 12,
                color: "rgba(255,255,255,0.65)",
                background: "none",
                border: "1px solid rgba(255,255,255,0.14)",
                borderRadius: 6,
                padding: "4px 12px",
                cursor: "pointer",
              }}
            >
              Cancelar plano
            </button>
          </div>
        )}

        <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 10, marginTop: 10 }}>
          <button
            onClick={() => navigate("/excluir-conta")}
            style={{
              fontSize: 12,
              color: "rgba(248,113,113,0.75)",
              background: "none",
              border: "1px solid rgba(248,113,113,0.25)",
              borderRadius: 6,
              padding: "4px 12px",
              cursor: "pointer",
            }}
          >
            Excluir minha conta e dados
          </button>
        </div>
      </div>

      <div
        style={{
          marginTop: 16,
          background: "rgba(251,191,36,0.10)",
          border: "1px solid rgba(251,191,36,0.25)",
          borderRadius: 10,
          padding: "12px 16px",
          fontSize: 12.5,
          color: "#fcd34d",
          lineHeight: 1.6,
        }}
      >
        <strong>ℹ️ Sobre automação Meta:</strong> A Meta descontinuou a API oficial de grupos do
        Facebook em abril de 2024. Para <strong>Páginas</strong>, posts são feitos via Graph API
        quando as credenciais estão configuradas. Para <strong>grupos</strong>, o sistema usa a
        fila com delay configurável.
      </div>
      </div>
    </div>
  );
}

/* ─── Sub-componentes ─── */

function SectionLabel({ children }) {
  return (
    <div
      style={{
        fontSize: 10.5,
        fontWeight: 700,
        color: "rgba(255,255,255,0.6)",
        letterSpacing: "0.07em",
        textTransform: "uppercase",
        marginBottom: 8,
      }}
    >
      {children}
    </div>
  );
}

function ConnCard({ emoji, label, badge, action }) {
  return (
    <div
      style={{
        background: "rgba(255,255,255,0.04)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 12,
        padding: "14px 14px 12px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 20, lineHeight: 1 }}>{emoji}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: "#e4e4e7" }}>{label}</span>
      </div>
      <div>
        <span
          style={{
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: "0.05em",
            color: badge.color,
            background: badge.color + "18",
            borderRadius: 4,
            padding: "2px 7px",
          }}
        >
          {badge.text}
        </span>
      </div>
      {action && (
        <button
          onClick={action.busy ? undefined : action.onClick}
          disabled={action.busy}
          style={{
            fontSize: 11.5,
            fontWeight: 600,
            color: action.outline ? "rgba(255,255,255,0.5)" : "#60a5fa",
            background: action.outline ? "transparent" : "rgba(59,130,246,0.1)",
            border: action.outline
              ? "1px solid #3f3f46"
              : "1px solid rgba(59,130,246,0.3)",
            borderRadius: 6,
            padding: "4px 10px",
            cursor: action.busy ? "not-allowed" : "pointer",
            alignSelf: "flex-start",
            opacity: action.busy ? 0.5 : 1,
          }}
        >
          {action.label}
        </button>
      )}
    </div>
  );
}

function MetricCard({ icon, label, value, suffix, color, isText, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: "rgba(255,255,255,0.04)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 12,
        padding: "14px 16px",
        cursor: onClick ? "pointer" : "default",
        transition: "border-color 0.15s",
        boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
      }}
      onMouseEnter={(e) => {
        if (onClick) e.currentTarget.style.borderColor = color + "60";
      }}
      onMouseLeave={(e) => {
        if (onClick) e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
      }}
    >
      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", marginBottom: 6 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
        <span style={{ fontSize: 22, fontWeight: 700, color }}>
          {value === null ? "—" : isText ? value : value?.toLocaleString("pt-BR")}
        </span>
        {suffix && value !== null && (
          <span style={{ fontSize: 13, color: "rgba(255,255,255,0.55)" }}>{suffix}</span>
        )}
      </div>
      <div style={{ marginTop: 6, fontSize: 18 }}>{icon}</div>
    </div>
  );
}

function ShortcutBtn({ label, icon: Icon, route, color }) {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(route)}
      style={{
        background: "rgba(255,255,255,0.04)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 12,
        padding: "14px 10px",
        cursor: "pointer",
        textAlign: "center",
        transition: "all 0.15s",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 8,
        boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = color + "60";
        e.currentTarget.style.background = color + "0f";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
        e.currentTarget.style.background = "rgba(255,255,255,0.04)";
      }}
    >
      <Icon size={20} style={{ color }} />
      <span style={{ fontSize: 11.5, fontWeight: 600, color: "#d4d4d8", lineHeight: 1.3 }}>
        {label}
      </span>
    </button>
  );
}

function AccountRow({ label, value, mono }) {
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "baseline", minWidth: 0 }}>
      <span style={{ color: "rgba(255,255,255,0.55)", flexShrink: 0 }}>{label}:</span>
      <span
        style={{
          color: "#d4d4d8",
          fontFamily: mono ? "monospace" : undefined,
          fontSize: mono ? 11 : undefined,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minWidth: 0,
        }}
      >
        {value || "—"}
      </span>
    </div>
  );
}
